/** Where a thing a community buys belongs: food, or one of three kinds of supplies. It only
    sorts what a person reads; it never changes what is searched, filed, priced or bought. */
export const SHOP_SECTIONS = ["food", "kitchen", "housekeeping", "general"] as const;
export type ShopSection = (typeof SHOP_SECTIONS)[number];

export const SHOP_SECTION_LABEL: Record<ShopSection, string> = {
  food: "Food",
  kitchen: "Kitchen supplies",
  housekeeping: "Housekeeping supplies",
  general: "General supplies",
};

/** What a list or the graph can be narrowed to: one section, or every supply at once. */
export const SHOP_SECTION_FILTERS = [
  "food",
  "supplies",
  "kitchen",
  "housekeeping",
  "general",
] as const;
export type ShopSectionFilter = (typeof SHOP_SECTION_FILTERS)[number];

export const SHOP_SECTION_FILTER_LABEL: Record<ShopSectionFilter, string> = {
  ...SHOP_SECTION_LABEL,
  supplies: "All supplies",
};

export const inSectionFilter = (section: ShopSection, filter: ShopSectionFilter): boolean =>
  filter === "supplies" ? section !== "food" : section === filter;

/* Groups not listed are food. A new supply group must be listed: the graph test fails when its
   kinds' names read as supplies under a group read as food. */
const SUPPLY_GROUP_SECTION: Readonly<Record<string, ShopSection>> = {
  "group:cleaning": "housekeeping",
  "group:paper-goods": "housekeeping",
  "group:disposables": "kitchen",
  "group:kitchen-supplies": "kitchen",
  "group:personal-care": "general",
  "group:incontinence": "general",
  "group:medical": "general",
  "group:office": "general",
};

/** A kind kept apart from its group's section. */
const KIND_SECTION: Readonly<Record<string, ShopSection>> = {
  "en:dish-soap": "kitchen",
};

/** A group or kind of the graph, by its slug and its group's: a kind its own way first, then its
    group's way; anything else is food. */
export function sectionOfConcept(slug: string, groupSlug: string | null): ShopSection {
  return KIND_SECTION[slug] ?? SUPPLY_GROUP_SECTION[groupSlug ?? slug] ?? "food";
}

/** The items under each section that has any, food first, each keeping the order it came in. */
export function bySection<T>(
  items: readonly T[],
  sectionOf: (item: T) => ShopSection,
): { section: ShopSection; items: T[] }[] {
  const groups = new Map<ShopSection, T[]>(SHOP_SECTIONS.map((section) => [section, []]));
  for (const item of items) groups.get(sectionOf(item))?.push(item);
  return SHOP_SECTIONS.flatMap((section) => {
    const held = groups.get(section) ?? [];
    return held.length > 0 ? [{ section, items: held }] : [];
  });
}
